<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
      <div class="text-center">
        <div class="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto mb-4"></div>
        <p class="text-lg text-gray-600">Initializing Email Sprocket...</p>
      </div>
    </div>

    <!-- Main app layout - only show when authenticated AND not loading -->
    <div v-else-if="isAuthenticated" class="flex">
      <!-- Sidebar -->
      <div class="w-64 bg-white shadow-lg border-r border-gray-200 flex flex-col">
        <div class="flex-1">
          <div class="p-6">
            <h1 class="text-2xl font-bold text-gray-900">Email Sprocket</h1>
            <p class="text-sm text-gray-600 mt-1">{{ partner?.PartnerName }}</p>
          </div>
          
          <!-- Navigation -->
          <nav class="mt-6">
            <router-link
              v-for="item in navigationItems"
              :key="item.name"
              :to="item.path"
              class="flex items-center px-6 py-3 text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors duration-200"
              :class="{ 'bg-primary-50 text-primary-600 border-r-2 border-primary-600': $route.path === item.path }"
            >
              <component :is="item.icon" class="w-5 h-5 mr-3" />
              {{ item.name }}
            </router-link>
          </nav>
        </div>
        
        <!-- Partner Selector - Bottom of Sidebar -->
        <div class="border-t border-gray-200 p-4">
          <label class="block text-xs font-medium text-gray-500 mb-2">Current Partner</label>
          <select
            v-model="selectedPartnerId"
            @change="onPartnerChange"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white mb-2"
            :disabled="switchingPartner || accessiblePartners.length <= 1"
          >
            <option
              v-for="p in accessiblePartners"
              :key="p.PartnerID"
              :value="p.PartnerID"
            >
              {{ p.PartnerName }}
            </option>
          </select>
          <button
            @click="showAddPartnerModal = true"
            class="w-full mt-2 px-3 py-2 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors duration-200 flex items-center justify-center"
          >
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Partner
          </button>
          <p v-if="accessiblePartners.length > 1" class="text-xs text-gray-400 mt-2">
            {{ accessiblePartners.length }} partner(s) available
          </p>
          <p v-else class="text-xs text-gray-400 mt-2">
            Only one partner available
          </p>
        </div>
        
        <!-- Add Partner Modal -->
        <div v-if="showAddPartnerModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div class="relative bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">Add New Partner</h3>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Partner Name</label>
              <input
                v-model="newPartnerName"
                type="text"
                class="input-field w-full"
                placeholder="Enter partner name"
                @keyup.enter="createPartner"
              />
            </div>
            <div class="flex items-center justify-end space-x-3">
              <button
                @click="showAddPartnerModal = false; newPartnerName = ''"
                class="btn-secondary"
              >
                Cancel
              </button>
              <button
                @click="createPartner"
                :disabled="!newPartnerName || creatingPartner"
                class="btn-primary"
              >
                <span v-if="creatingPartner" class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </span>
                <span v-else>Create</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main content -->
      <div class="flex-1 flex flex-col">
        <!-- Header -->
        <header class="bg-white shadow-sm border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-semibold text-gray-900">
              {{ currentPageTitle }}
            </h2>
            <div class="flex items-center space-x-4">
              <button
                @click="logout"
                class="text-gray-600 hover:text-gray-900 transition-colors duration-200"
              >
                <ArrowRightOnRectangleIcon class="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        <!-- Page content with smooth transitions -->
        <main class="flex-1 p-6">
          <transition
            name="page"
            mode="out-in"
            appear
          >
            <router-view
              :key="$route.path"
              class="animate-expand"
            />
          </transition>
        </main>
      </div>
    </div>

    <!-- Auth0 login - only show when not authenticated and not loading -->
    <div v-else class="flex items-center justify-center min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      <div class="text-center">
        <h1 class="text-4xl font-bold text-primary-900 mb-4">Email Sprocket</h1>
        <p class="text-lg text-primary-700 mb-8">Professional email campaign management</p>
        <button
          @click="login"
          class="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors duration-200 text-lg font-semibold"
        >
          Sign In
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useRouter } from 'vue-router'
import { usePartnerStore } from './stores/partner'
import { partnersApi } from './services/api'
import {
  HomeIcon,
  EnvelopeIcon,
  ListBulletIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/vue/24/outline'

// Updated for Auth0 Vue 2.4.0 - simplified destructuring
const { isAuthenticated, isLoading, loginWithRedirect, logout: auth0Logout, user } = useAuth0()
const router = useRouter()
const partnerStore = usePartnerStore()

// Debug Auth0 configuration
console.log('App.vue - Auth0 state:', { 
  isAuthenticated: isAuthenticated.value, 
  isLoading: isLoading.value,
  user: user.value
})
console.log('App.vue - Environment variables:', {
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  audience: import.meta.env.VITE_AUTH0_AUDIENCE
})

// Watch Auth0 state changes
watch(isLoading, (newLoading) => {
  console.log('Auth0 loading state changed:', newLoading)
})

watch(isAuthenticated, (newAuth) => {
  console.log('Auth0 authentication state changed:', newAuth)
  if (newAuth) {
    console.log('User authenticated, fetching partner data...')
    partnerStore.fetchPartner()
    partnerStore.fetchAccessiblePartners()
  }
})

const partner = computed(() => partnerStore.partner)
const accessiblePartners = computed(() => partnerStore.accessiblePartners)
// Initialize from localStorage if available
const storedPartnerId = localStorage.getItem('selectedPartnerId')
const selectedPartnerId = ref(storedPartnerId ? parseInt(storedPartnerId) : null)
const switchingPartner = ref(false)
const showAddPartnerModal = ref(false)
const newPartnerName = ref('')
const creatingPartner = ref(false)

// Watch for partner changes and update selector
watch(partner, (newPartner) => {
  if (newPartner) {
    const newPartnerId = newPartner.PartnerID
    selectedPartnerId.value = newPartnerId
    // Update localStorage to keep it in sync
    localStorage.setItem('selectedPartnerId', newPartnerId.toString())
  }
}, { immediate: true })

// Watch for accessible partners changes
watch(accessiblePartners, (newPartners) => {
  if (newPartners.length > 0) {
    // If we have a stored partner ID, check if it's in the accessible list
    if (storedPartnerId) {
      const storedId = parseInt(storedPartnerId)
      const found = newPartners.find(p => p.PartnerID === storedId)
      if (found) {
        selectedPartnerId.value = storedId
        return
      }
    }
    // Otherwise, use the current partner or first available
    if (!selectedPartnerId.value || !newPartners.find(p => p.PartnerID === selectedPartnerId.value)) {
      selectedPartnerId.value = partner.value?.PartnerID || newPartners[0].PartnerID
    }
  }
}, { immediate: true })

const onPartnerChange = async () => {
  if (selectedPartnerId.value && selectedPartnerId.value !== partner.value?.PartnerID) {
    try {
      switchingPartner.value = true
      await partnerStore.switchPartner(selectedPartnerId.value)
    } catch (error) {
      console.error('Error switching partner:', error)
      // Reset selector to current partner
      selectedPartnerId.value = partner.value?.PartnerID
    } finally {
      switchingPartner.value = false
    }
  }
}

const createPartner = async () => {
  if (!newPartnerName.value || newPartnerName.value.trim() === '') return
  
  try {
    creatingPartner.value = true
    const response = await partnersApi.create({ partnerName: newPartnerName.value.trim() })
    
    if (response.data.success) {
      // Refresh accessible partners list
      await partnerStore.fetchAccessiblePartners()
      // Switch to the new partner
      await partnerStore.switchPartner(response.data.data.PartnerID)
      // Close modal
      showAddPartnerModal.value = false
      newPartnerName.value = ''
    }
  } catch (error) {
    console.error('Error creating partner:', error)
    // Error toast is handled by API interceptor
  } finally {
    creatingPartner.value = false
  }
}

// Navigation items
const navigationItems = [
  { name: 'Dashboard', path: '/', icon: HomeIcon },
  { name: 'Campaigns', path: '/campaigns', icon: EnvelopeIcon },
  { name: 'Lists', path: '/lists', icon: ListBulletIcon },
  { name: 'Domains', path: '/domains', icon: GlobeAltIcon },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon }
]

// Current page title
const currentPageTitle = computed(() => {
  const currentRoute = navigationItems.find(item => item.path === router.currentRoute.value.path)
  return currentRoute ? currentRoute.name : 'Dashboard'
})

// Login function - updated for v2.4.0 with callback route
const login = async () => {
  console.log('Login button clicked')
  console.log('Auth0 state:', { isAuthenticated: isAuthenticated.value, isLoading: isLoading.value })
  
  try {
    await loginWithRedirect({
      authorizationParams: {
        redirect_uri: `${window.location.origin}/callback`,
        scope: 'openid profile email offline_access' // Include offline_access for refresh tokens
      }
    })
    console.log('Login redirect initiated')
  } catch (error) {
    console.error('Login error:', error)
  }
}

// Logout function - updated for v2.4.0
const logout = () => {
  auth0Logout({
    logoutParams: {
      returnTo: window.location.origin
    }
  })
}

// Simplified initialization for v2.4.0
onMounted(async () => {
  console.log('App.vue - onMounted, checking authentication state')
  
  // Wait for Auth0 to finish loading
  if (isLoading.value) {
    console.log('Waiting for Auth0 to finish loading...')
    let attempts = 0
    const maxAttempts = 20 // 10 seconds max wait
    
    while (isLoading.value && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 500))
      attempts++
      console.log(`Waiting for Auth0 to finish loading... attempt ${attempts}`)
    }
    
    if (isLoading.value) {
      console.log('Auth0 still loading after timeout, proceeding anyway')
    } else {
      console.log('Auth0 finished loading')
    }
  }
  
  console.log('Final Auth0 state:', { 
    isAuthenticated: isAuthenticated.value, 
    isLoading: isLoading.value,
    user: user.value
  })
  
  // If user is authenticated, fetch partner data
  if (isAuthenticated.value) {
    console.log('User is authenticated, fetching partner data...')
    await partnerStore.fetchPartner()
    await partnerStore.fetchAccessiblePartners()
  } else {
    console.log('User is not authenticated')
  }
})
</script>

<style>
/* Page transition animations */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style> 
